import React, { useState } from 'react';
import { supabase } from '../../../services/supabase';
import { Loader2, Package, Share2, Clock, FileText, LineChart, Search, Star, MessageSquare } from 'lucide-react';
import TiboLogo from '../../../components/assets/tibo-logo.svg';
import HeroImage from '../../../assets/images/_85fb3a5a-06d9-45c4-ac0a-66e42a7f1146.jpeg';
import { LandingV2 } from '../../../features/landing/LandingV2';

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [showLandingV2, setShowLandingV2] = useState(false);
  const [showCatalogExample, setShowCatalogExample] = useState(false);

  const handleGoogleLogin = async () => {
    try {
      console.log('🚀 Iniciando login con Google...');
      console.log('🌍 URL de redirección:', process.env.NODE_ENV === 'production' 
        ? 'https://tibodigital.com'
        : 'http://localhost:3000');
      
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
  
      console.log('🔑 Respuesta de auth:', { 
        success: !error,
        data: data ? {
          provider: data.provider,
          url: data.url
        } : null,
        error: error ? {
          message: error.message,
          status: error.status
        } : null
      });
  
      if (error) throw error;
    } catch (err) {
      console.error('❌ Error detallado del login:', {
        message: err.message,
        name: err.name,
        stack: err.stack,
        cause: err.cause
      });
      setError('Error al iniciar sesión. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (showLandingV2) {
    return <LandingV2 onShowCatalog={() => setShowCatalogExample(true)} />;
  }

  if (showCatalogExample) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        {/* Botón para volver */}
        <button
          onClick={() => setShowCatalogExample(false)}
          className="fixed top-4 left-4 px-4 py-2 text-sm bg-white rounded-lg shadow-sm hover:shadow-md transition-all z-50"
        >
          ← Volver al landing
        </button>

        <div className="max-w-md mx-auto pt-16">
          {/* Catálogo de ejemplo */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b z-10">
              <div className="flex items-center justify-between p-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold">Mercado Central</h1>
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  </div>
                  <p className="text-sm text-gray-500">Catálogo digital</p>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white border-b px-4 py-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar productos..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 rounded-xl text-gray-900 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B7C5B]"
                />
              </div>
            </div>

            {/* Products */}
            <div className="p-4 space-y-3 overflow-auto max-h-[600px]">
              {[
                { 
                  name: 'Tomates Perita',
                  price: '$950',
                  unit: 'cajón',
                  stock: 15,
                  category: 'Verduras',
                  image: 'bg-red-100',
                  featured: true
                },
                { 
                  name: 'Papas Blancas',
                  price: '$750',
                  unit: 'bolsa',
                  stock: 8,
                  category: 'Verduras',
                  image: 'bg-yellow-100'
                },
                { 
                  name: 'Cebollas',
                  price: '$600',
                  unit: 'bolsa',
                  stock: 20,
                  category: 'Verduras',
                  image: 'bg-purple-100'
                },
                { 
                  name: 'Manzanas Red',
                  price: '$1200',
                  unit: 'cajón',
                  stock: 12,
                  category: 'Frutas',
                  image: 'bg-red-50'
                },
                { 
                  name: 'Naranjas',
                  price: '$850',
                  unit: 'bolsa',
                  stock: 25,
                  category: 'Frutas',
                  image: 'bg-orange-100',
                  featured: true
                },
                { 
                  name: 'Zanahorias',
                  price: '$450',
                  unit: 'bolsa',
                  stock: 30,
                  category: 'Verduras',
                  image: 'bg-orange-50'
                },
                { 
                  name: 'Limones',
                  price: '$600',
                  unit: 'bolsa',
                  stock: 18,
                  category: 'Frutas',
                  image: 'bg-yellow-50'
                },
                { 
                  name: 'Berenjenas',
                  price: '$800',
                  unit: 'cajón',
                  stock: 10,
                  category: 'Verduras',
                  image: 'bg-purple-50'
                }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-sm border">
                  <div className="flex gap-4">
                    <div className={`w-16 h-16 ${item.image} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Package className="w-8 h-8 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-xs text-[#3B7C5B] font-medium">{item.category}</div>
                        {item.featured && (
                          <Star className="w-4 h-4 text-[#3B7C5B] fill-current" />
                        )}
                      </div>
                      <h3 className="font-medium text-lg">{item.name}</h3>
                      <div className="text-sm text-gray-500 space-x-2">
                        <span className="text-[#3B7C5B] font-bold">{item.price}/{item.unit}</span>
                        <span>•</span>
                        <span>Stock: {item.stock}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Bar */}
            <div className="sticky bottom-0 bg-white border-t p-4">
              <button className="w-full bg-[#25D366] text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Contactar por WhatsApp
              </button>
            </div>
          </div>

          {/* Floating Update Badge */}
          <div className="fixed top-4 right-4 bg-white px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">En vivo</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Botón discreto para Landing V2 */}
      <button
        onClick={() => setShowLandingV2(true)}
        className="fixed top-4 right-4 px-3 py-1 text-sm text-gray-500 hover:text-gray-700 bg-white/50 hover:bg-white rounded-md shadow-sm"
      >
        Ver Landing V2
      </button>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center space-y-12 mb-16">
          <img src={TiboLogo} alt="Tibó" className="h-16 mx-auto" />
          
          {/* Headline */}
          <div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              Simple como siempre.<br/>
              Poderosa como nunca.
            </h1>
            <p className="mt-6 text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
              Tibó es una herramienta ágil y fácil de usar<br/>
              para que vos y tu equipo lleven a tu negocio al siguiente nivel.
            </p>
          </div>

          {/* CTA Button */}
          <div className="mt-12">
            <div className="mb-4 space-y-2">
              <p className="text-sm text-gray-600">
                Empezá tu prueba gratuita con un click
              </p>
              <p className="text-sm text-gray-500">
                Sin compromisos. Sin tarjeta de crédito.
              </p>
            </div>

            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full max-w-md mx-auto flex items-center justify-center gap-3 px-6 py-4 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-md"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continuar con Google
                </>
              )}
            </button>

            {/* Error Message */}
            {error && (
              <div className="mt-4 text-center text-red-600 text-sm">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Image Section */}
        <div className="mt-20">
          <div className="relative max-w-4xl mx-auto">
            <div className="aspect-[4/3] rounded-xl overflow-hidden">
              <img 
                src={HeroImage} 
                alt="Mayorista usando Tibó en su negocio" 
                className="w-full h-full object-cover"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/40 to-transparent" />
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="mt-24 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Herramientas simples<br/>
            para negocios serios
          </h2>
          <p className="text-xl md:text-2xl text-gray-600">
            Diseñadas específicamente para el comercio de alimentos frescos.
          </p>
          <p className="text-xl md:text-2xl text-gray-600">
            Listo para usar por dueños de negocios, vendedores, cajeras, peones y changarines.
          </p>
        </div>

        {/* Benefits Section */}
        <div className="mt-24">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start space-x-4 p-6">
              <div className="flex-shrink-0">
                <Package className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Control preciso de stock
                </h3>
                <p className="text-gray-600 text-lg">
                  Tu mercadería siempre actualizada y trazabilidad total de tus lotes.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4 p-6">
              <div className="flex-shrink-0">
                <FileText className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Ventas ágiles con tickets impresos
                </h3>
                <p className="text-gray-600 text-lg">
                Del pedido al remito en un click. Impresión instantánea y sin errores.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6">
              <div className="flex-shrink-0">
                <Share2 className="h-8 w-8 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Catálogo digital con un click
                </h3>
                <p className="text-gray-600 text-lg">
                Compartí todo tu catálogo con tus clientes mediante un simple link.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6">
              <div className="flex-shrink-0">
                <LineChart className="h-8 w-8 text-orange-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Tu negocio, en la palma de tu mano
                </h3>
                <p className="text-gray-600 text-lg">
                Toda la información crítica, cuando más la necesitás. 
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        {/*
        <div className="mt-24">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Elegí el plan ideal para tu negocio
            </h2>
            <p className="text-xl text-gray-600">
              Probá Tibó GRATIS durante 7 días. Sin tarjeta de crédito. Sin compromiso.
            </p>
            
            {/* Billing Toggle *//*}
            <div className="flex items-center justify-center gap-4 mt-8">
              <span className={`text-lg ${!isAnnual ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Mensual
              </span>
              <button
                onClick={() => setIsAnnual(!isAnnual)}
                className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isAnnual ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-lg ${isAnnual ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Anual (20% off)
              </span>
            </div>
          </div>

          {/* Pricing Cards *//*}
          <div className="grid grid-cols-12 gap-6 max-w-[1400px] mx-auto px-6">
            {/* Emprendedor Card *//*}
            <div className="col-span-12 md:col-span-2 relative bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col">
              <div>
                <h3 className="text-lg font-bold text-gray-900 truncate">Emprendedor</h3>
                <p className="text-sm text-gray-600 mt-1">Para empezar con todo</p>
              </div>
              
              <div className="mt-4">
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900">
                    ${isAnnual ? '16.000' : '20.000'}
                  </span>
                  <span className="text-sm text-gray-600 ml-2">/mes</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">${isAnnual ? '19.360' : '24.200'} con IVA</p>
                {isAnnual && (
                  <p className="text-sm text-green-600 mt-1">Ahorrás $48.000 al año</p>
                )}
              </div>

              <div className="mt-4 flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">1 usuario</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Gestión de inventario</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Soporte por WhatsApp</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Equipo Card *//*}
            <div className="col-span-12 md:col-span-2 relative bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col">              
              <div>
                <h3 className="text-lg font-bold text-gray-900 truncate">Equipo</h3>
                <p className="text-sm text-gray-600 mt-1">Ideal para equipos pequeños</p>
              </div>
              
              <div className="mt-4">
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900">
                    ${isAnnual ? '40.000' : '50.000'}
                  </span>
                  <span className="text-sm text-gray-600 ml-2">/mes</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">${isAnnual ? '48.400' : '60.500'} con IVA</p>
                {isAnnual && (
                  <p className="text-sm text-green-600 mt-1">Ahorrás $120.000 al año</p>
                )}
              </div>

              <div className="mt-4 flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Hasta 3 usuarios</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Todo lo de Emprendedor</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Soporte prioritario</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Reportes básicos</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Plus Card - Popular *//*}
            <div className="col-span-12 md:col-span-4 relative bg-white rounded-xl shadow-lg border-2 border-green-500 p-5 flex flex-col scale-[1.02] z-10">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">Más popular</span>
              </div>
              
              <div>
                <h3 className="text-lg font-bold text-gray-900 truncate">Plus</h3>
                <p className="text-sm text-gray-600 mt-1">Para equipos en crecimiento</p>
              </div>
              
              <div className="mt-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-gray-500 line-through">
                    ${isAnnual ? '160.000' : '200.000'}
                  </span>
                  <span className="text-4xl font-bold text-gray-900">
                    ${isAnnual ? '80.000' : '100.000'}
                  </span>
                  <span className="text-sm text-gray-600">/mes</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">${isAnnual ? '96.800' : '121.000'} con IVA</p>
                <div className="flex gap-2 items-center mt-1">
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded">50% OFF</span>
                  <span className="text-sm text-green-600">PROMO LANZAMIENTO. 13 suscripciones restantes</span>
                </div>
                {isAnnual && (
                  <p className="text-sm text-green-600 mt-1">Ahorrás $240.000 al año</p>
                )}
              </div>

              <div className="mt-4 flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Hasta 5 usuarios</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Todo lo de Equipo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Reportes avanzados</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Impresora térmica</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-600">Personalización avanzada</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Pro Card *//*}
            <div className="col-span-12 md:col-span-2 relative bg-gray-900 rounded-xl shadow-lg p-5 flex flex-col text-white">
              <div>
                <h3 className="text-lg font-bold truncate">Pro</h3>
                <p className="text-sm text-gray-300 mt-1">Para equipos grandes y avanzados</p>
              </div>
              
              <div className="mt-4">
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold">
                    ${isAnnual ? '240.000' : '300.000'}
                  </span>
                  <span className="text-sm text-gray-300 ml-2">/mes</span>
                </div>
                <p className="text-sm text-gray-300 mt-1">${isAnnual ? '290.400' : '363.000'} con IVA</p>
                {isAnnual && (
                  <p className="text-sm text-green-400 mt-1">Ahorrás $720.000 al año</p>
                )}
              </div>

              <div className="mt-4 flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-300">Hasta 10 usuarios</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-300">Todo lo de Plus</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-300">API access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-300">Soporte dedicado 24/7</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-gray-300">Impresora térmica</span>
                  </li>
                </ul>
              </div>

              <a 
                href="mailto:contacto@tibodigital.com" 
                className="mt-6 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                Contactar ventas
              </a>
            </div>

            {/* Master Card *//*}
            <div className="col-span-12 md:col-span-2 relative bg-green-600 rounded-xl shadow-lg p-5 flex flex-col text-white">
              <div>
                <h3 className="text-lg font-bold truncate">Master</h3>
                <p className="text-sm text-green-100 mt-1">Plan personalizado</p>
              </div>
              
              <div className="mt-4">
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold">
                    Personalizado
                  </span>
                </div>
                <p className="text-sm text-green-100 mt-1">Adaptado a tus necesidades</p>
              </div>

              <div className="mt-4 flex-grow">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-white mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-green-100">Usuarios ilimitados</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-white mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-green-100">Todo lo de Pro</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-white mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-green-100">Funcionalidades a medida</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-white mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-green-100">Soporte VIP</span>
                  </li>
                </ul>
              </div>

              <a
                href="https://wa.me/5491164707065"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-gray-900 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                Contactar por WhatsApp
              </a>
            </div>
          </div>
        </div>
        */}

        {/* Foundation Section */}
        <div className="mt-32 border-t border-green-500 pt-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600">
              Fundación Tibó
            </h2>
          </div>

          <div className="max-w-6xl mx-auto px-4">
            <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 md:p-12">
              <div className="max-w-4xl mx-auto text-center space-y-6">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                  ¿Y si fuera posible la donación descentralizada de alimentos que beneficiara a todas las partes?
                </h3>
                
                <div className="space-y-6 text-lg text-gray-600">
                  <p>
                    El primer experimento de la fundación Tibó junto a la asociación civil Plato Lleno durante 2021/2022 
                    logró validar que el modelo funcionaba: se distribuyó el equivalente a <span className="font-semibold text-green-600">100.000 platos de comida</span> (350g de alimento fresco) 
                    beneficiando a donantes, transportistas y comedores y escuelas que recibieron los alimentos de manera 100% gratuita.
                  </p>
                  
                  <p>
                    El objetivo para el 2025 es alcanzar el equivalente a <span className="font-semibold text-green-600">un millón de platos</span> de comida 
                    distribuidos de manera económicamente viable para todas las partes.
                  </p>
                </div>

                <div className="mt-12 space-y-6">
                  <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-gray-900">¿Por qué es importante?</h3>
                    <div className="space-y-4">
                      <p className="text-xl text-gray-800">
                        En Argentina, <span className="font-semibold text-red-600">7 de cada 10 menores de 18 años</span> no tienen garantizado 
                        su derecho básico a la alimentación, mientras que el desperdicio alcanza <span className="font-semibold text-red-600">16 millones de toneladas anuales</span>.<br/><br/>
                        La solución existe: usando tecnología y creatividad logística e impositiva, podemos conectar el excedente con quienes más lo necesitan, beneficiando a todos los participantes y mejorando la calidad de vida de millones de personas.
                      </p>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900">¿Tenés ideas? Sumate</h3>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <a
                      href="https://wa.me/5491164707065?text=Hola,%20leí%20acerca%20de%20la%20fundación%20Tibó"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors duration-200 gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Contactanos por WhatsApp
                    </a>

                    <a
                      href="https://www.instagram.com/tiboapp/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-pink-500 via-purple-500 to-orange-500 hover:from-pink-600 hover:via-purple-600 hover:to-orange-600 transition-all duration-200 gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                      Mirá las fotos
                    </a>
                  </div>
                </div>

                {/* Impact Metrics */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-green-600 mb-2">100K</div>
                    <div className="text-gray-600">Platos de comida distribuidos</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-green-600 mb-2">350g</div>
                    <div className="text-gray-600">Alimento fresco por plato</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-green-600 mb-2">1M</div>
                    <div className="text-gray-600">Objetivo de platos para 2025</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Terms */}
        <div className="mt-16 text-center">
          <p className="text-sm text-gray-500">
            Al iniciar sesión, aceptas nuestros{' '}
            <a href="#" className="text-blue-600 hover:underline">
              Términos de Servicio
            </a>{' '}
            y{' '}
            <a href="#" className="text-blue-600 hover:underline">
              Política de Privacidad
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;